import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#F9F9F7]/80 backdrop-blur-md border-b border-[#E5E5E5]/50">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex justify-between items-center h-20">
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-home">
            {t('home')}
          </Link>
          <Link to="/shop" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-shop">
            {t('shop')}
          </Link>
          <Link to="/about" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-about">
            {t('about')}
          </Link>
          <Link to="/contact" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-contact">
            {t('contact')}
          </Link>
        </div>

        {/* Right Side Icons */}
        <div className="flex items-center gap-4 ml-auto">
          <LanguageSwitcher />
          
          {user ? (
            <div className="hidden md:flex items-center gap-4">
              {user.is_admin && (
                <Link to="/admin" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-admin">
                  ADMIN
                </Link>
              )}
              <Link to="/account" className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-account">
                <User size={20} />
              </Link>
              <button 
                onClick={logout} 
                className="text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors"
                data-testid="nav-logout-btn"
              >
                {t('logout')}
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden md:block text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#C6A87C] transition-colors" data-testid="nav-login">
              {t('login')}
            </Link>
          )}

          <Link to="/cart" className="relative" data-testid="nav-cart">
            <ShoppingCart size={20} className="text-[#1A1A1A]" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C6A87C] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                {cartCount}
              </span>
            )}
          </Link>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="md:hidden"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#F9F9F7] border-t border-[#E5E5E5] py-4 px-6" data-testid="mobile-menu">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-home">
            {t('home')}
          </Link>
          <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-shop">
            {t('shop')}
          </Link>
          <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-about">
            {t('about')}
          </Link>
          <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-contact">
            {t('contact')}
          </Link>
          {user ? (
            <>
              {user.is_admin && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-admin">
                  ADMIN
                </Link>
              )}
              <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-account">
                {t('account')}
              </Link>
              <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-logout">
                {t('logout')}
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A]" data-testid="mobile-nav-login">
              {t('login')}
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;